import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Review, Vehicle } from "@/types";

export interface BookedRange {
  start: string;
  end: string;
}

interface BookingRow {
  start_date: string | null;
  end_date: string | null;
  status: string | null;
}

export function useVehicle(id: number | null) {
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [bookedRanges, setBookedRanges] = useState<BookedRange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) {
      setLoading(false);
      setError("We couldn't find that car.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: vehicleError } = await supabase
        .from("vehicles")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (vehicleError) throw vehicleError;
      if (!data) {
        setVehicle(null);
        setError("This car is no longer available.");
        return;
      }
      setVehicle(data as Vehicle);

      const [reviewRes, bookingRes] = await Promise.all([
        supabase.from("reviews").select("*").eq("vehicle_id", id).order("created_at", { ascending: false }),
        supabase.from("bookings").select("start_date, end_date, status").eq("vehicle_id", id),
      ]);

      setReviews((reviewRes.data as Review[]) ?? []);

      const rows = (bookingRes.data as BookingRow[] | null) ?? [];
      setBookedRanges(
        rows
          .filter((row) => row.status !== "cancelled" && row.start_date && row.end_date)
          .map((row) => ({ start: row.start_date as string, end: row.end_date as string }))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this car right now.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  return { vehicle, reviews, bookedRanges, loading, error, reload: load };
}